import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../axiosSetup";
import moment from "moment";
import DataTable from "./DataTable";
import cfg from "../../config";

const NotificationList = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const res = await api.get("/api/notification-zunftrat", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (Array.isArray(res.data) && res.data.length > 0) {
          setNotifications(res.data);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchNotifications();
  }, []);

  // Die Schnittstelle liefert neutrale Feldnamen; die Beschriftung entsteht
  // hier, damit sie den Begriffen aus der Konfiguration folgen kann.
  const BESCHRIFTUNG = useMemo(() => ({
    ID:          'ID',
    priority:    'Priorität',
    title:       'Titel',
    body:        'Beschreibung',
    createdBy:   'Erstellt von',
    createdAt:   'Erstellt am',
    publishDate: 'Veröffentlichung',
    forEveryone: 'Alle benachrichtigen',
    forCouncil:  `${cfg.adminRoleName} benachrichtigen`,
    member:      cfg.memberTerm,
    sendMail:    'E-Mail versenden?',
  }), []);

  const columns = useMemo(() => {
    if (!notifications.length) return [];
    return Object.keys(notifications[0]).map((key) => ({
      key,
      label: BESCHRIFTUNG[key] ?? key,
      render: (val) => {
        if (val == null) return <span className="dt-muted">—</span>;
        if (key === "member" && typeof val === "object") {
          return val.name || <span className="dt-muted">—</span>;
        }
        if (key === "priority") {
          const prio = { 1: "Sehr wichtig", 2: "Wichtig", 3: "Normal" };
          const colors = { 1: "#ef4444", 2: "#f97316", 3: "#22c55e" };
          const n = Number(val);
          return (
            <span style={{
              padding: "2px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700,
              whiteSpace: "nowrap", display: "inline-block",
              background: (colors[n] ?? "#64748b") + "22",
              color: colors[n] ?? "#64748b",
              border: `1px solid ${colors[n] ?? "#64748b"}`,
            }}>
              {prio[n] ?? val}
            </span>
          );
        }
        if (typeof val === "boolean" || val === 1 || val === 0) {
          return val
            ? <span className="dt-badge dt-badge-yes">Ja</span>
            : <span className="dt-badge dt-badge-no">Nein</span>;
        }
        const s = String(val);
        if (/^\d{4}-\d{2}-\d{2}(T.*)?$/.test(s) && moment(s).isValid()) {
          return moment(s).format("DD.MM.YYYY HH:mm");
        }
        return s;
      },
      searchValue: (val) => {
        if (val == null) return "";
        if (key === "member" && typeof val === "object") return val.name ?? "";
        return String(val);
      },
    }));
  }, [notifications, BESCHRIFTUNG]);

  const actions = (notification) => (
    <div className="dt-actions">
      <button
        className="dt-btn dt-btn-primary"
        onClick={() =>
          navigate("/zr-notification-create", {
            state: {
              notification: {
                ...notification,
                memberID: notification.memberID ? [notification.memberID] : [],
              },
            },
          })
        }
      >
        Bearbeiten
      </button>
    </div>
  );

  const headerActions = (
    <button className="dt-btn dt-btn-success" onClick={() => navigate("/zr-notification-create")}>
      + Mitteilung erstellen
    </button>
  );

  return (
    <div className="container-fluid px-3 px-md-4 mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Mitteilungen</h2>
      </div>
      <DataTable
        data={notifications}
        columns={columns}
        loading={loading}
        error={error}
        actions={actions}
        headerActions={headerActions}
        searchPlaceholder="Mitteilung suchen…"
        emptyText="Keine Mitteilungen vorhanden."
        initialPageSize={25}
        pageSizeOptions={[10, 25, 50]}
      />
      <br />
    </div>
  );
};

export default NotificationList;
